import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useGameStore } from "../../store/gameStore";
import { api } from "../../lib/api";
import "./Market.css";

// ─── Types ─────────────────────────────────────────────────────────────────────

interface Listing {
  id: string;
  seller_id: string;
  resource_id: string;
  quantity: number;
  price_per_unit: number;
  status: string;
  created_at: string;
  seller_username: string;
  seller_avatar_id: string;
}

interface FeedEntry {
  id: string;
  resource_id: string;
  quantity: number;
  total_gold: number;
  created_at: string;
  buyer_username: string;
  seller_username: string;
}

interface InventoryEntry {
  resource_id: string;
  quantity: number;
}

// ─── Resource metadata ─────────────────────────────────────────────────────────

const RESOURCE_META: Record<string, { name: string; emoji: string }> = {
  muorji:     { name: "Multebær",  emoji: "🫐" },
  muorra:     { name: "Ved",       emoji: "🪵" },
  gámasgárvi: { name: "Skinn",     emoji: "🦌" },
  náhppi:     { name: "Snøre",     emoji: "🧵" },
  silki:      { name: "Ullgarn",   emoji: "🧶" },
  sarvi:      { name: "Horn",      emoji: "🦴" },
};

const RESOURCE_IDS = Object.keys(RESOURCE_META);

function resourceName(id: string) { return RESOURCE_META[id]?.name ?? id; }
function resourceEmoji(id: string) { return RESOURCE_META[id]?.emoji ?? "📦"; }

// ─── Helpers ───────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "akkurat nå";
  if (diff < 3600) return `${Math.floor(diff / 60)} min siden`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} t siden`;
  return `${Math.floor(diff / 86400)} d siden`;
}

// ─── Toast ─────────────────────────────────────────────────────────────────────

function Toast({ msg, error }: { msg: string; error?: boolean }) {
  return <div className={`toast${error ? " toast--error" : ""}`}>{msg}</div>;
}

// ─── MarketScreen ──────────────────────────────────────────────────────────────

type Tab = "buy" | "sell";

export function MarketScreen() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const gold = useGameStore((s) => s.playerGold);
  const setGold = useGameStore((s) => s.setGold);

  const [tab, setTab] = useState<Tab>("buy");
  const [listings, setListings] = useState<Listing[]>([]);
  const [feed, setFeed] = useState<FeedEntry[]>([]);
  const [inventory, setInventory] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ msg: string; error?: boolean } | null>(null);

  // Sell form state
  const [sellResource, setSellResource] = useState(RESOURCE_IDS[0]);
  const [sellQty, setSellQty] = useState(1);
  const [sellPrice, setSellPrice] = useState(10);
  const [listing, setListing] = useState(false);

  // Buy state
  const [buyingId, setBuyingId] = useState<string | null>(null);
  const [buyQtys, setBuyQtys] = useState<Record<string, number>>({});

  function showToast(msg: string, error = false) {
    setToast({ msg, error });
    setTimeout(() => setToast(null), 2700);
  }

  async function loadMarket() {
    const [marketRes, invRes] = await Promise.all([
      api.get<{ listings: Listing[]; feed: FeedEntry[] }>("/market"),
      token ? api.get<{ inventory: InventoryEntry[] }>("/inventory", token) : Promise.resolve(null),
    ]);

    if (marketRes.ok) {
      setListings(marketRes.data.listings);
      setFeed(marketRes.data.feed);
    }

    if (invRes && invRes.ok) {
      const map: Record<string, number> = {};
      for (const e of invRes.data.inventory) map[e.resource_id] = e.quantity;
      setInventory(map);
    }

    setLoading(false);
  }

  useEffect(() => {
    void loadMarket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ── Buy tab ────────────────────────────────────────────────────────────────

  function getBuyQty(listingId: string, max: number) {
    return Math.min(buyQtys[listingId] ?? 1, max);
  }

  async function handleBuy(listing: Listing) {
    if (!token) return;
    const quantity = getBuyQty(listing.id, listing.quantity);
    const total = quantity * listing.price_per_unit;

    if (gold < total) {
      showToast("Ikke nok gull! 💸", true);
      return;
    }

    setBuyingId(listing.id);
    const res = await api.post<{ quantity: number; totalGold: number; newGold: number }>(
      "/market/buy",
      { listingId: listing.id, quantity },
      token
    );
    setBuyingId(null);

    if (res.ok) {
      setGold(res.data.newGold);          // sync authoritative value from server
      showToast(`Kjøpte ${quantity}× ${resourceName(listing.resource_id)}! 🎉`);
      void loadMarket();
    } else {
      const msgMap: Record<string, string> = {
        INSUFFICIENT_GOLD: "Ikke nok gull!",
        INSUFFICIENT_QUANTITY: "Ikke nok på lager",
        LISTING_NOT_FOUND: "Oppslaget finnes ikke lenger",
      };
      showToast(msgMap[res.error ?? ""] ?? "Noe gikk galt", true);
    }
  }

  // ── Sell tab ───────────────────────────────────────────────────────────────

  async function handleList() {
    if (!token) return;
    const avail = inventory[sellResource] ?? 0;
    if (avail < sellQty) {
      showToast("Ikke nok på lager", true);
      return;
    }

    setListing(true);
    const res = await api.post<{ listingId: string }>(
      "/market/list",
      { resourceId: sellResource, quantity: sellQty, pricePerUnit: sellPrice },
      token
    );
    setListing(false);

    if (res.ok) {
      showToast(`${sellQty}× ${resourceName(sellResource)} lagt ut! ✅`);
      setSellQty(1);
      void loadMarket();
    } else {
      const msgMap: Record<string, string> = {
        INVALID_RESOURCE: "Ugyldig ressurs",
        INSUFFICIENT_INVENTORY: "Ikke nok ressurser",
      };
      showToast(msgMap[res.error ?? ""] ?? "Noe gikk galt", true);
    }
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  // Group listings by resource for Buy tab
  const listingsByResource = listings.reduce<Record<string, Listing[]>>((acc, l) => {
    (acc[l.resource_id] ??= []).push(l);
    return acc;
  }, {});

  const sellAvail = inventory[sellResource] ?? 0;

  return (
    <div className="market-page">
      {/* Header */}
      <div className="market-page__header">
        <button className="market-page__back" onClick={() => navigate("/map")}>
          ←
        </button>
        <span className="market-page__title">Marked</span>
        <div className="gold-badge">
          <span>🪙</span>
          <span>{gold}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="market-tabs">
        <button
          className={`market-tab${tab === "buy" ? " market-tab--active" : ""}`}
          onClick={() => setTab("buy")}
        >
          Kjøp
        </button>
        <button
          className={`market-tab${tab === "sell" ? " market-tab--active" : ""}`}
          onClick={() => setTab("sell")}
        >
          Selg
        </button>
        <button
          className={`market-tab${tab === ("feed" as Tab) ? " market-tab--active" : ""}`}
          onClick={() => setTab("feed" as Tab)}
        >
          Handel
        </button>
      </div>

      {/* Body */}
      <div className="market-body">
        {loading ? (
          <p className="market-empty">Laster…</p>
        ) : tab === "buy" ? (
          <>
            {Object.keys(listingsByResource).length === 0 ? (
              <p className="market-empty">Ingen aktive oppslag ennå.</p>
            ) : (
              RESOURCE_IDS.filter((id) => listingsByResource[id]?.length).map((resId) => (
                <section key={resId}>
                  <p className="market-section-label">
                    {resourceEmoji(resId)} {resourceName(resId)}
                  </p>
                  {listingsByResource[resId].map((l) => {
                    const qty = getBuyQty(l.id, l.quantity);
                    const total = qty * l.price_per_unit;
                    const isMine = l.seller_id === user?.userId;
                    const canAfford = gold >= total;
                    const isLoading = buyingId === l.id;

                    return (
                      <div className="listing-card" key={l.id}>
                        <div className="listing-card__icon">
                          {resourceEmoji(l.resource_id)}
                        </div>
                        <div className="listing-card__info">
                          <p className="listing-card__name">
                            {resourceName(l.resource_id)}
                          </p>
                          <p className="listing-card__meta">
                            <span>Fra {l.seller_username}</span>
                            <span>Lager: {l.quantity}</span>
                          </p>
                        </div>
                        <div className="listing-card__right">
                          <div className="listing-price">
                            <span>🪙</span>
                            <span>{l.price_per_unit}/stk</span>
                          </div>
                          {/* Qty stepper inline */}
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <button
                              className="qty-stepper__btn"
                              disabled={qty <= 1}
                              onClick={() =>
                                setBuyQtys((p) => ({ ...p, [l.id]: Math.max(1, qty - 1) }))
                              }
                            >
                              −
                            </button>
                            <span className="qty-stepper__val">{qty}</span>
                            <button
                              className="qty-stepper__btn"
                              disabled={qty >= l.quantity}
                              onClick={() =>
                                setBuyQtys((p) => ({ ...p, [l.id]: Math.min(l.quantity, qty + 1) }))
                              }
                            >
                              +
                            </button>
                          </div>
                          <div className="listing-total">Total: 🪙 {total}</div>
                          <button
                            className="btn-buy-listing"
                            disabled={isMine || !canAfford || isLoading}
                            onClick={() => handleBuy(l)}
                          >
                            {isMine ? "Ditt" : isLoading ? "…" : !canAfford ? "Ikke råd" : "Kjøp"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </section>
              ))
            )}
          </>
        ) : tab === "sell" ? (
          <>
            {/* Sell form */}
            <div className="sell-form">
              <div className="sell-form__row">
                <label className="sell-form__label">Ressurs</label>
                <select
                  className="sell-form__select"
                  value={sellResource}
                  onChange={(e) => { setSellResource(e.target.value); setSellQty(1); }}
                >
                  {RESOURCE_IDS.map((id) => (
                    <option key={id} value={id}>
                      {resourceEmoji(id)} {resourceName(id)} (du har {inventory[id] ?? 0})
                    </option>
                  ))}
                </select>
                {sellAvail === 0 && (
                  <span className="sell-form__avail">Du har ingen {resourceName(sellResource)}</span>
                )}
              </div>

              <div className="qty-stepper">
                <span className="qty-stepper__label">Mengde</span>
                <button
                  className="qty-stepper__btn"
                  disabled={sellQty <= 1}
                  onClick={() => setSellQty((q) => Math.max(1, q - 1))}
                >
                  −
                </button>
                <span className="qty-stepper__val">{sellQty}</span>
                <button
                  className="qty-stepper__btn"
                  disabled={sellQty >= sellAvail}
                  onClick={() => setSellQty((q) => Math.min(sellAvail, q + 1))}
                >
                  +
                </button>
              </div>

              <div className="sell-form__row">
                <label className="sell-form__label">Pris per enhet (gull)</label>
                <input
                  type="number"
                  className="sell-form__input"
                  min={1}
                  value={sellPrice}
                  onChange={(e) => setSellPrice(Math.max(1, Number(e.target.value)))}
                />
              </div>

              <button
                className="btn-list"
                disabled={listing || sellAvail < sellQty || sellQty < 1 || sellPrice < 1}
                onClick={handleList}
              >
                {listing ? "Legger ut…" : `Legg ut ${sellQty}× for 🪙 ${sellPrice * sellQty} totalt`}
              </button>
            </div>

            {/* Inventory overview */}
            <p className="market-section-label">Ditt lager</p>
            {RESOURCE_IDS.filter((id) => (inventory[id] ?? 0) > 0).length === 0 ? (
              <p className="market-empty">Ingen ressurser på lager ennå.</p>
            ) : (
              RESOURCE_IDS.filter((id) => (inventory[id] ?? 0) > 0).map((id) => (
                <div className="inv-row" key={id}>
                  <span className="inv-row__icon">{resourceEmoji(id)}</span>
                  <span className="inv-row__name">{resourceName(id)}</span>
                  <span className="inv-row__qty">×{inventory[id]}</span>
                </div>
              ))
            )}
          </>
        ) : (
          /* Feed tab */
          <>
            <p className="market-section-label">Siste handler</p>
            {feed.length === 0 ? (
              <p className="market-empty">Ingen handler ennå.</p>
            ) : (
              feed.map((entry) => (
                <div className="feed-item" key={entry.id}>
                  <span className="feed-item__icon">{resourceEmoji(entry.resource_id)}</span>
                  <div className="feed-item__body">
                    <p className="feed-item__line">
                      <strong>{entry.buyer_username}</strong> kjøpte{" "}
                      {entry.quantity}× {resourceName(entry.resource_id)} fra{" "}
                      <strong>{entry.seller_username}</strong> for{" "}
                      <span style={{ color: "#c8971a" }}>🪙 {entry.total_gold}</span>
                    </p>
                    <p className="feed-item__time">{timeAgo(entry.created_at)}</p>
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>

      {toast && <Toast msg={toast.msg} error={toast.error} />}
    </div>
  );
}

// Re-export gold-badge style used in Shop too — both pages share the pattern
// (defined in Market.css since Shop.css has its own copy)
